// ROTAS69 native top free-shipping bar (sections/r69-freeship-bar.liquid).
// On a cart change, re-fetch THIS section through the Section Rendering API and swap the inner
// content, so the remaining-amount message recomputes server-side with the correct market
// currency (same source of truth as the cart-drawer bar). The host element persists across the
// swap, so the listener stays wired.
import { StandardEvents } from '@shopify/events';

class FreeShippingBar extends HTMLElement {
  connectedCallback() {
    this._onCart = (event) => {
      // The event carries a promise that resolves once the cart mutation is committed; wait for it
      // so the section re-render reads the NEW cart total.
      const done = event && event.promise ? event.promise : Promise.resolve();
      done.then(() => this.refresh()).catch(() => {});
    };
    document.addEventListener(StandardEvents.cartLinesUpdate, this._onCart);

    // The page HTML is edge-cached, so the server-rendered amount can be stale (it reflects the
    // cart at cache time, not the visitor's real cart — same reason cart-icon re-checks the count).
    // Hydrate once from the live cart via the Section Rendering API so the bar is always accurate.
    const hydrate = () => this.refresh();
    if ('requestIdleCallback' in window) requestIdleCallback(hydrate, { timeout: 1200 });
    else setTimeout(hydrate, 200);
  }

  disconnectedCallback() {
    if (this._onCart) document.removeEventListener(StandardEvents.cartLinesUpdate, this._onCart);
    if (this._controller) this._controller.abort();
  }

  async refresh() {
    const id = this.dataset.sectionId;
    if (!id) return;

    if (this._controller) this._controller.abort();
    this._controller = new AbortController();

    try {
      const url = `${window.location.pathname}?section_id=${encodeURIComponent(id)}`;
      const res = await fetch(url, { signal: this._controller.signal });
      if (!res.ok) return;

      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const fresh = doc.querySelector('[data-fsbar-inner]');
      const current = this.querySelector('[data-fsbar-inner]');
      if (!current) return;

      if (fresh) {
        current.replaceWith(fresh);
        // Keep the host's reached/state classes in sync with the freshly rendered markup.
        const freshHost = doc.querySelector('free-shipping-bar');
        if (freshHost) this.className = freshHost.className;
      } else {
        // No tier for the country / bar turned off → nothing to show.
        current.remove();
      }
    } catch (error) {
      if (error && error.name !== 'AbortError') console.warn('[r69-freeship-bar]', error);
    }
  }
}

if (!customElements.get('free-shipping-bar')) {
  customElements.define('free-shipping-bar', FreeShippingBar);
}
